import React from "react";

/**
 * Amstrad GX4000 controller — wireframe ("fil de fer") 3D view.
 * Rectangular body with rounded corners, viewed in 3/4 perspective.
 * Pure SVG neon stroke outlines, blueprint background.
 */
export const Gx4000Pad = ({ className = "" }) => {
  // 3D perspective offsets (depth)
  const dx = 18; // horizontal depth offset
  const dy = 14; // vertical depth offset

  // Front-face rectangle (rounded), points reused for back face by adding (dx, -dy)
  // Front face corners:
  // TL (60, 130) TR (560, 130) BR (560, 290) BL (60, 290), corner radius r
  const fx1 = 60, fy1 = 130, fx2 = 560, fy2 = 290, r = 22;

  // Back face corners = front + (dx, -dy)
  const bx1 = fx1 + dx, by1 = fy1 - dy;
  const bx2 = fx2 + dx, by2 = fy2 - dy;

  return (
    <svg
      data-testid="gx4000-pad"
      viewBox="0 0 640 400"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Amstrad GX4000 controller — wireframe view"
    >
      <defs>
        <filter id="neon" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="2.2" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id="neonSoft" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="0.9" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <pattern id="bpGrid" width="32" height="32" patternUnits="userSpaceOnUse">
          <path d="M32 0H0V32" fill="none" stroke="rgba(0,255,255,0.08)" strokeWidth="0.5" />
        </pattern>
      </defs>

      {/* Blueprint grid */}
      <rect x="0" y="0" width="640" height="400" fill="url(#bpGrid)" />

      {/* Axis markers */}
      <g stroke="rgba(0,255,255,0.25)" strokeWidth="0.5" fontFamily="VT323, monospace" fontSize="9" fill="rgba(0,255,255,0.6)">
        <line x1="20" y1="20" x2="60" y2="20" />
        <line x1="20" y1="20" x2="20" y2="60" />
        <text x="62" y="22">X</text>
        <text x="22" y="62">Y</text>
        <text x="618" y="390" textAnchor="end">GX4000 / WIRE-002</text>
      </g>

      {/* Cable behind body — emerges from back-right edge */}
      <g stroke="#00ffff" strokeWidth="1.4" fill="none" filter="url(#neonSoft)" strokeLinecap="round">
        <path d={`M ${bx2 - 90} ${by1 + 4} C ${bx2 - 30} ${by1 - 20}, ${bx2 + 10} ${by1 - 40}, ${bx2 + 30} ${by1 - 60}`} />
        {Array.from({ length: 7 }).map((_, i) => {
          const cx = bx2 + 32 + i * 9;
          const cy = by1 - 60 - i * 8;
          return <ellipse key={i} cx={cx} cy={cy} rx="10" ry="4.5" transform={`rotate(-28 ${cx} ${cy})`} />;
        })}
        <path d={`M ${bx2 + 96} ${by1 - 116} L ${bx2 + 110} ${by1 - 126}`} />
      </g>

      {/* Back face (rectangle with rounded corners) */}
      <g stroke="rgba(0,255,255,0.55)" strokeWidth="1.1" fill="none" strokeDasharray="4 4" filter="url(#neonSoft)">
        <rect x={bx1} y={by1} width={fx2 - fx1} height={fy2 - fy1} rx={r} ry={r} />
      </g>

      {/* Connecting edges (front-back) — 4 corners */}
      <g stroke="#00ffff" strokeWidth="1.2" fill="none" filter="url(#neonSoft)" opacity="0.9">
        <line x1={fx1 + r * 0.3} y1={fy1 + r * 0.3} x2={bx1 + r * 0.3} y2={by1 + r * 0.3} />
        <line x1={fx2 - r * 0.3} y1={fy1 + r * 0.3} x2={bx2 - r * 0.3} y2={by1 + r * 0.3} />
        <line x1={fx2 - r * 0.3} y1={fy2 - r * 0.3} x2={bx2 - r * 0.3} y2={by2 - r * 0.3} />
        <line x1={fx1 + r * 0.3} y1={fy2 - r * 0.3} x2={bx1 + r * 0.3} y2={by2 - r * 0.3} />
      </g>

      {/* Front face — main rectangle */}
      <g stroke="#00ffff" strokeWidth="1.9" fill="rgba(0,40,50,0.18)" filter="url(#neon)" strokeLinejoin="round">
        <rect x={fx1} y={fy1} width={fx2 - fx1} height={fy2 - fy1} rx={r} ry={r} />
      </g>

      {/* Inner panel groove (subtle stylized seam on the top face, purely decorative) */}
      <g stroke="rgba(0,255,255,0.45)" strokeWidth="0.8" fill="none" strokeDasharray="2 4" filter="url(#neonSoft)">
        <rect x={fx1 + 12} y={fy1 + 12} width={fx2 - fx1 - 24} height={fy2 - fy1 - 24} rx={r - 6} ry={r - 6} />
      </g>

      {/* D-pad assembly (left) */}
      <g filter="url(#neon)">
        <ellipse cx="170" cy="210" rx="78" ry="60" fill="rgba(0,40,50,0.30)" stroke="#00ffff" strokeWidth="1.6" />
        <ellipse cx="170" cy="210" rx="58" ry="44" fill="none" stroke="#00ffff" strokeWidth="1" opacity="0.55" />
        <g stroke="#00ffff" strokeWidth="1.4" fill="none" strokeLinejoin="round">
          <path d="
            M 158 172
            L 182 172
            L 182 198
            L 208 198
            L 208 222
            L 182 222
            L 182 248
            L 158 248
            L 158 222
            L 132 222
            L 132 198
            L 158 198
            Z
          " />
          <polyline points="170,178 166,184 174,184" />
          <polyline points="170,242 166,236 174,236" />
          <polyline points="138,210 144,206 144,214" />
          <polyline points="202,210 196,206 196,214" />
        </g>
        <circle cx="170" cy="210" r="4" fill="none" stroke="#00ffff" strokeWidth="1.2" />
      </g>

      {/* Amstrad logo plate (center-right) */}
      <g filter="url(#neonSoft)">
        <rect x="312" y="232" width="92" height="32" fill="none" stroke="#00ffff" strokeWidth="1.2" />
        <text x="350" y="254" textAnchor="middle" fontFamily="VT323, monospace" fontSize="18" fill="#00ffff" letterSpacing="2">AMSTRAD</text>
        <rect x="388" y="236" width="12" height="12" fill="none" stroke="#ff0044" strokeWidth="1.3" />
        <line x1="388" y1="236" x2="400" y2="248" stroke="#ff0044" strokeWidth="0.8" />
        <line x1="400" y1="236" x2="388" y2="248" stroke="#ff0044" strokeWidth="0.8" />
      </g>
      <text x="358" y="278" textAnchor="middle" fontFamily="VT323, monospace" fontSize="9" fill="rgba(0,255,255,0.55)" letterSpacing="3">GX4000</text>

      {/* Button 1 (cylindrical, 3/4 view) */}
      <g filter="url(#neon)">
        <ellipse cx="448" cy="222" rx="28" ry="22" fill="none" stroke="#ff0044" strokeWidth="1.5" />
        <ellipse cx="448" cy="214" rx="22" ry="17" fill="rgba(255,0,68,0.10)" stroke="#ff0044" strokeWidth="1.4" />
        <line x1="420" y1="222" x2="426" y2="214" stroke="#ff0044" strokeWidth="1.2" />
        <line x1="476" y1="222" x2="470" y2="214" stroke="#ff0044" strokeWidth="1.2" />
        <path d="M 436 206 Q 446 200 458 206" fill="none" stroke="#ffe600" strokeWidth="1" opacity="0.85" />
        <text x="448" y="266" textAnchor="middle" fontFamily="VT323, monospace" fontSize="20" fill="#ff0044">1</text>
      </g>

      {/* Button 2 */}
      <g filter="url(#neon)">
        <ellipse cx="514" cy="232" rx="28" ry="22" fill="none" stroke="#ff0044" strokeWidth="1.5" />
        <ellipse cx="514" cy="224" rx="22" ry="17" fill="rgba(255,0,68,0.10)" stroke="#ff0044" strokeWidth="1.4" />
        <line x1="486" y1="232" x2="492" y2="224" stroke="#ff0044" strokeWidth="1.2" />
        <line x1="542" y1="232" x2="536" y2="224" stroke="#ff0044" strokeWidth="1.2" />
        <path d="M 502 216 Q 512 210 524 216" fill="none" stroke="#ffe600" strokeWidth="1" opacity="0.85" />
        <text x="514" y="278" textAnchor="middle" fontFamily="VT323, monospace" fontSize="20" fill="#ff0044">2</text>
      </g>

      {/* Annotations */}
      <g stroke="rgba(0,255,255,0.55)" strokeWidth="0.7" fill="none" fontFamily="VT323, monospace" fontSize="10">
        <line x1="170" y1="146" x2="170" y2="110" />
        <line x1="170" y1="110" x2="240" y2="110" />
        <text x="244" y="113" fill="rgba(0,255,255,0.85)">D-PAD ◂▴▾▸</text>

        <line x1="480" y1="196" x2="480" y2="160" />
        <line x1="480" y1="160" x2="600" y2="160" />
        <text x="600" y="156" textAnchor="end" fill="rgba(255,0,68,0.85)">FIRE 1 / FIRE 2</text>

        <line x1="540" y1="80" x2="470" y2="80" />
        <text x="466" y="82" textAnchor="end" fill="rgba(0,255,255,0.85)">9-PIN CABLE</text>

        <text x="60" y="370" fill="rgba(0,255,255,0.45)">SCHEMATIC // AMSTRAD GX4000 CONTROL PAD — 1990</text>
      </g>
    </svg>
  );
};

export default Gx4000Pad;
